import React, { useState } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled, { css } from 'styled-components';
import {
  brandFonts,
  standardColours,
  greyColours,
  brandColours,
  fontSize,
  inputStyles,
  selectWrapStyles,
  selectStyles,
  minBreakpointQuery,
  maxBreakpointQuery,
  visuallyHidden,
  errorStyles,
  selectErrorStyles,
} from '../styles';
import { Container, Button, Link, Svg } from './ui';
import Badge from './Badge';
import { removeValidation, submitForm, useValidation } from '../utils';
import youtubeIcon from '../images/youtube-icon.svg';
import twitterIcon from '../images/twitter-icon.svg';
import facebookIcon from '../images/facebook-icon.svg';
import instagramIcon from '../images/instagram-icon.svg';
import AcceptedPayments from './checkout/AcceptedPayments';

const StyledFooter = styled.footer`
  position: relative;
  overflow: hidden;
  background-color: ${brandColours.primary};
  color: ${standardColours.white};
`;

const StyledContainer = styled(Container)`
  position: relative;
`;

const StyledBadge = styled(Badge)`
  position: absolute;
  right: 0;
  bottom: 20%;
  height: 600px;
  width: 600px;
  fill: ${standardColours.white};
  opacity: 0.08;

  ${maxBreakpointQuery.mlarge`
    bottom: 20%;
    transform: translateX(50%) translateY(50%);
  `}

  ${minBreakpointQuery.mlarge`
    bottom: 0;
    transform: translateY(38%);
  `}
`;

const StyledInner = styled.div`
  position: relative;
`;

const StyledNewsletter = styled.div`
  padding: 50px 0;
  display: grid;
  align-items: center;
  justify-content: center;
  gap: 30px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: calc(50% - 50vw);
    width: 100vw;
    border-bottom: 1px solid ${brandColours.octonary};
  }

  ${minBreakpointQuery.mlarge`
    padding: 70px;
  `}
`;

const StyledContent = styled.div`
  display: grid;
  justify-items: center;
  text-align: center;
`;

const StyledNewsletterHeading = styled.h3`
  ${brandFonts.secondary};
  ${fontSize(40)};
  line-height: 1;
`;

const StyledText = styled.p`
  color: ${brandColours.tridenary};

  ${({ alt }) => {
    if (alt) {
      return css`
        margin-top: 15px;

        ${minBreakpointQuery.mlarge`
          ${fontSize(18)};
          margin-top: 25px;
        `}
      `;
    } else {
      return css`
        ${fontSize(14)};

        ${minBreakpointQuery.mlarge`
          display: inline-block;
          margin-left: 20px;
        `}
      `;
    }
  }}
`;

const StyledForm = styled.form`
  display: grid;
  gap: 5px;

  ${minBreakpointQuery.medium`
    display: flex;
    width: 100%;
    max-width: 980px;
  `}
`;

const StyledField = styled.label`
  ${({ honeypot }) => {
    if (honeypot) {
      return css`
        ${visuallyHidden()};
      `;
    }
  }}
  ${errorStyles()};
`;

const StyledFullField = styled.label`
  ${errorStyles()};
`;

const StyledSelectField = styled.label`
  ${errorStyles()};
`;

const StyledLabel = styled.span`
  ${visuallyHidden()};
`;

const StyledInput = styled.input`
  ${inputStyles()};
  height: 56px;
`;

const StyledSelectWrap = styled.span`
  ${selectWrapStyles()};
  ${selectErrorStyles()};
`;

const StyledSelect = styled.select`
  ${selectStyles()};
  height: 56px;

  option {
    color: ${brandColours.primary};
  }

  ${({ selected }) => {
    if (!selected) {
      return css`
        color: ${greyColours.darkPurplishGrey};
      `;
    }
  }}
`;

const StyledButton = styled(Button)`
  white-space: nowrap;

  ${minBreakpointQuery.mlarge`
    min-width: 120px;
  `}
`;

const StyledTop = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 20px;

  ${minBreakpointQuery.medium`
    margin: 75px 0;
  `};

  ${minBreakpointQuery.mlarge`
    margin: 95px 0;
    grid-template-columns: 2fr repeat(2, 1fr) 1.5fr;
    column-gap: 40px;
  `}
`;

const StyledGroup = styled.div`
  position: relative;

  ${maxBreakpointQuery.medium`
    padding: 25px 0;

    &:first-child {
      grid-column: 1 / span 2;
    }

    &:first-child::after,
    &:last-child::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: calc(50% - 50vw);
      width: 100vw;
      border-bottom: 1px solid ${brandColours.octonary};
    }
  `};

  ${minBreakpointQuery.small`
    &:last-child {
      margin-bottom: 0;
    }
  `};

  ${minBreakpointQuery.medium`
    margin-bottom: 25px;
  `};

  &:first-child > ul:nth-child(2) {
    column-count: 2;
    column-gap: 40px;
  }
`;

const StyledHeading = styled.h3`
  margin-bottom: 14px;
  font-weight: 400;
  ${fontSize(20)};

  ${minBreakpointQuery.smedium`
    ${fontSize(18)};
    margin-bottom: 20px;
  `};

  ${minBreakpointQuery.mlarge`
    margin-bottom: 24px;
  `}
`;

const StyledList = styled.ul``;

const StyledListItem = styled.li`
  margin-top: 10px;
  color: ${brandColours.tridenary};
  ${fontSize(15)};

  &:first-child {
    margin-top: 0;
  }
`;

const StyledListLink = styled(Link)``;

const StyledHours = styled.p`
  margin: 10px 0;
  color: ${brandColours.tridenary};
  ${fontSize(15)};
`;

const StyledContactGroup = styled.div`
  grid-column: 1 / 3;

  ${minBreakpointQuery.medium`
    grid-column: 2 / 3;
    grid-row: 1 / 4;
  `}

  ${minBreakpointQuery.mlarge`
    grid-column: auto;
    grid-row: auto;
  `}
`;

const StyledContactWrapper = styled.div`
  ${maxBreakpointQuery.medium`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    align-items: center;
  `}
`;

const StyledContact = styled.div`
  text-align: right;
`;

const StyledMobileHeading = styled.p`
  ${maxBreakpointQuery.medium`
    ${brandFonts.tertiary};
    ${fontSize(32)};
    line-height: 1;
  `}

  ${minBreakpointQuery.medium`
    ${visuallyHidden()};
  `}
`;

const StyledContactText = styled.p`
  margin-bottom: 2px;
  color: ${brandColours.octonary};
  ${fontSize(13)};

  ${minBreakpointQuery.small`
    margin-bottom: 3px;
    ${fontSize(14)};
  `};

  span {
    position: relative;
    padding-left: 14px;

    &:before {
      content: '';
      position: absolute;
      top: 6px;
      left: 0;
      height: 7px;
      width: 7px;
      background-color: ${brandColours.quaternary};
      border-radius: 50%;

      ${minBreakpointQuery.small`
        top: 7px;
      `};
    }
  }
`;

const StyledPhone = styled.a`
  ${fontSize(22)};

  ${minBreakpointQuery.small`
    ${fontSize(24)};
  `}
`;

const StyledAcceptedPayments = styled(AcceptedPayments)`
  margin: 15px 0;

  ${minBreakpointQuery.medium`
    text-align: right;
  `}
`;

const StyledSocials = styled.div`
  margin-top: 10px;
  display: flex;
  gap: 10px;
  justify-content: center;

  ${minBreakpointQuery.medium`
    display: grid;
    grid-template-columns: repeat(2, auto);
    justify-content: end;
  `}
`;

const StyledSocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid ${brandColours.octonary};

  &:hover {
    background-color: ${brandColours.octonary};
  }
`;

const StyledIcon = styled(Svg)`
  height: 16px;
  width: 16px;
  vertical-align: middle;
`;

const StyledBottom = styled.div`
  margin: 25px 0;

  ${maxBreakpointQuery.mlarge`
    display: grid;
    justify-items: center;
    gap: 8px;
    text-align: center;
  `}

  ${minBreakpointQuery.mlarge`
    display: flex;
    justify-content: space-between;
    align-items: center;
  `}
`;

const StyledBottomContent = styled.div`
  ${minBreakpointQuery.mlarge`
    display: flex;
    align-items: center;
  `}
`;

const StyledBottomHeading = styled.p`
  ${maxBreakpointQuery.medium`
    ${visuallyHidden()};
  `}

  ${minBreakpointQuery.medium`
    ${brandFonts.tertiary};
    ${fontSize(45)};
  `}

  ${minBreakpointQuery.mlarge`
    ${fontSize(35)};
    display: inline-block;
  `}
`;

const Footer = ({ removeNewsletter, altTheme, isError }) => {
  const {
    spaseekers: {
      layout: {
        footer: {
          menu: { helpfulLinks, legalInformation },
          openingHours,
        },
      },
      site: { telephone },
    },
  } = useStaticQuery(graphql`
    query FooterQuery {
      spaseekers {
        layout {
          footer {
            menu {
              helpfulLinks {
                text
                url
              }
              legalInformation {
                text
                url
              }
            }
            openingHours {
              weekday
              saturday
              sunday
            }
          }
        }
        site {
          telephone
        }
      }
    }
  `);

  const navLinks = [
    {
      heading: 'Helpful Links',
      links: helpfulLinks,
    },
    {
      heading: 'Legal Information',
      links: legalInformation,
    },
  ];

  const contactInformation = {
    youtubeProfile: 'https://www.youtube.com/user/spaseekers',
    twitterProfile: 'https://twitter.com/spaseekers',
    facebookProfile: 'https://www.facebook.com/spaseekers',
    instagramProfile: 'https://www.instagram.com/spaseekers',
  };

  // Validation
  const requiredFields = [
    { name: 'firstName', type: 'input' },
    { name: 'lastName', type: 'input' },
    { name: 'email', type: 'input' },
    { name: 'regionId', type: 'select' },
  ];

  useValidation(requiredFields);

  const formName = 'newsletter-form';

  const [selectedRegion, setSelectedRegion] = useState();

  return (
    <StyledFooter altTheme={altTheme} isError={isError}>
      <StyledContainer>
        <StyledBadge />
        <StyledInner>
          {!removeNewsletter && (
            <StyledNewsletter>
              <StyledContent>
                <StyledNewsletterHeading>
                  Receive our latest deals &amp; offers
                </StyledNewsletterHeading>
                <StyledText alt={true}>
                  For exclusive deals, news and offers sign up to our
                  newsletter.
                </StyledText>
              </StyledContent>
              <StyledForm
                id={formName}
                name={formName}
                method="POST"
                onSubmit={e => {
                  submitForm(e, formName, 'forms/newsletter');
                }}
              >
                <StyledField honeypot={true}>
                  <StyledLabel>Details</StyledLabel>
                  <StyledInput type="text" name="details" />
                </StyledField>
                <StyledField>
                  <StyledLabel>First Name</StyledLabel>
                  <StyledInput
                    type="text"
                    name="firstName"
                    placeholder="First Name"
                    onChange={e => removeValidation(e)}
                    required
                  />
                </StyledField>
                <StyledField>
                  <StyledLabel>Last Name</StyledLabel>
                  <StyledInput
                    type="text"
                    name="lastName"
                    placeholder="Last Name"
                    onChange={e => removeValidation(e)}
                    required
                  />
                </StyledField>
                <StyledFullField>
                  <StyledLabel>Email</StyledLabel>
                  <StyledInput
                    type="email"
                    name="email"
                    placeholder="Email"
                    onChange={e => removeValidation(e)}
                    required
                  />
                </StyledFullField>
                <StyledSelectField>
                  <StyledLabel>Region</StyledLabel>
                  <StyledSelectWrap>
                    <StyledSelect
                      name="regionId"
                      required
                      selected={selectedRegion}
                      onChange={e => {
                        setSelectedRegion(true);
                        removeValidation(e);
                      }}
                    >
                      <option selected disabled value="">
                        Select region
                      </option>
                      <option value="4">East Anglia</option>
                      <option value="5">East Midlands</option>
                      <option value="389554">Guernsey</option>
                      <option value="13">Jersey</option>
                      <option value="1">London</option>
                      <option value="8">North East</option>
                      <option value="9">North West</option>
                      <option value="14">Northern Ireland</option>
                      <option value="10">Scotland</option>
                      <option value="3">South East</option>
                      <option value="2">South West</option>
                      <option value="11">Wales</option>
                      <option value="6">West Midlands</option>
                      <option value="7">Yorkshire</option>
                    </StyledSelect>
                  </StyledSelectWrap>
                </StyledSelectField>
                <StyledButton alt={true} type="submit">
                  Sign up
                </StyledButton>
              </StyledForm>
            </StyledNewsletter>
          )}
          <StyledTop>
            {navLinks.map(({ heading, links }, i) => (
              <StyledGroup key={`footer-link-${i}`}>
                <StyledHeading>{heading}</StyledHeading>
                <StyledList>
                  {links.map(({ text, url }, i) => (
                    <StyledListItem key={`footer-link-${i}`}>
                      <StyledListLink to={url}>{text}</StyledListLink>
                    </StyledListItem>
                  ))}
                </StyledList>
              </StyledGroup>
            ))}
            {openingHours && (
              <StyledGroup>
                <StyledHeading>Opening Hours</StyledHeading>
                <StyledHours>{openingHours.weekday}</StyledHours>
                <StyledHours>{openingHours.saturday}</StyledHours>
                <StyledHours>{openingHours.sunday}</StyledHours>
              </StyledGroup>
            )}
            <StyledContactGroup>
              {telephone && (
                <StyledContactWrapper>
                  <StyledMobileHeading>
                    Find your indulgence
                  </StyledMobileHeading>
                  <StyledContact>
                    <StyledContactText>
                      <span>Bookings &amp; Advice</span>
                    </StyledContactText>
                    <StyledPhone href={`tel:${telephone}`}>
                      {telephone}
                    </StyledPhone>
                  </StyledContact>
                </StyledContactWrapper>
              )}
              <StyledAcceptedPayments removeInfoText={true} />
              {(contactInformation.facebookProfile ||
                contactInformation.twitterProfile ||
                contactInformation.instagramProfile ||
                contactInformation.youtubeProfile) && (
                <StyledSocials>
                  {contactInformation.facebookProfile && (
                    <StyledSocialLink
                      href={contactInformation.facebookProfile}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <StyledIcon image={facebookIcon} title={`Facebook`} />
                    </StyledSocialLink>
                  )}
                  {contactInformation.twitterProfile && (
                    <StyledSocialLink
                      href={contactInformation.twitterProfile}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <StyledIcon image={twitterIcon} title={`Twitter`} />
                    </StyledSocialLink>
                  )}
                  {contactInformation.instagramProfile && (
                    <StyledSocialLink
                      href={contactInformation.instagramProfile}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <StyledIcon image={instagramIcon} title={`Instagram`} />
                    </StyledSocialLink>
                  )}
                  {contactInformation.youtubeProfile && (
                    <StyledSocialLink
                      href={contactInformation.youtubeProfile}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <StyledIcon image={youtubeIcon} title={`Youtube`} />
                    </StyledSocialLink>
                  )}
                </StyledSocials>
              )}
            </StyledContactGroup>
          </StyledTop>
          <StyledBottom>
            <StyledBottomContent>
              <StyledBottomHeading>Find your indulgence</StyledBottomHeading>
              <StyledText>
                All prices advertised are inclusive of VAT (Reg: 747 919 775)
              </StyledText>
            </StyledBottomContent>
            <StyledBottomContent>
              <StyledText>TripAdvisor {new Date().getFullYear()}</StyledText>
              <StyledText>
                &copy; SpaSeekers Ltd {new Date().getFullYear()}
              </StyledText>
            </StyledBottomContent>
          </StyledBottom>
        </StyledInner>
      </StyledContainer>
    </StyledFooter>
  );
};

export default Footer;
